import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import { isEmpty, isArray, defaultTo } from 'lodash';
import { RootState } from '../root/root-types';
import { ProductVariants } from '../products';
import { WhatsappTemplateData } from '@zonofi/common';
import {
  CampaignDataProps,
  CampaignDetails,
  RepeatTypeEnum,
  CampaignAudienceType,
  CampaignScheduleDetails,
} from './model';
import moment from 'moment';
import dayjs from 'dayjs';

export interface campaignState {
  selectedIds: number[];
  campaignProducts: ProductVariants[];
  headerSteps: { id: number; label: string }[];
  bodyPartKeysData: { key: string; label: string; value: string }[];
  ctwaInfo: { [key: string]: string };
  campaignType: string;
  mediafile: {
    path: string;
    type: string;
  } | null;
  templateData: WhatsappTemplateData | null;
  campaignName: string;
  templateCampaignType: string;
  screenRoute: string;
  campaignData: CampaignDataProps;
  campaignDetailsData: CampaignDetails | undefined;
  campaignAudienceType: CampaignAudienceType;
  campaignScheduleDetails: CampaignScheduleDetails | undefined;
}

export const getNextHalfHourTime = () => {
  const now = moment();

  const remainder = now.minutes() % 30;
  if (remainder !== 0) {
    now.add(30 - remainder, 'minutes');
  }

  now.seconds(0);

  return dayjs(now.toDate());
};

export const campaignInitialState: CampaignDataProps = {
  customersIds: [],
  selectedCampaignId: '',
  draftCustomerIds: [],
  draftProductIds: [],
  productsIds: [],
  assignedCustomerIds: [],
  assignedProductIds: [],
  assignedParsedCustomerIds: [],
  assignedParsedProductIds: [],
  dynamicSegmentIds: [],
  customerSegmentIds: [],
  draftTeamMemberIds: [],
  teamMembers: [],
  routeIds: [],
  campaignName: '',
  campaignDescription: '',
  templateDescription: '',
  scheduleId: '',
  campaignType: 'ORDER',
  productView: 'LIST',
  uri: '',
  imageUri: '',
  uploadFile: undefined,
  fileError: false,
  publishNow: false,
  whatsAddEnabled: false,
  scheduleEnable: false,
  teamMemberEnable: false,
  scheduleCustomerLevel: false,
  messages: [],
  AddId: '',
  postId: '',
  startDate: '',
  endDate: '',
  startTime: '',
  endTime: '',
  totalCustomers: 0,
  totalProducts: 0,
  productList: [],
  productsData: [],
  dateError: '',
  buttons: [],
  selectedBot: {
    botName: '',
    botId: '',
    status: false,
    triggers: '',
  },
  headerDetails: {
    campaignHeaderFormat: '',
    headerText: '',
    dataUrl: '',
  },
  selectedTab: 0,
  footerText: '',
  audienceType: 'static',
  campaignFrequencyEnable: false,
  botLibraryId: '',
  isBotEdit: false,
  openCampaignReccurence: {
    open: false,
    id: '',
  },
  campaignFrequency: {
    startDate: moment().tz('Asia/Kolkata').format('YYYY-MM-DD'),
    startTime: getNextHalfHourTime(),
    repeatFrequency: '1',
    repeatType: RepeatTypeEnum.DAY,
    endDate: moment().tz('Asia/Kolkata').add(100, 'years').format('YYYY-MM-DD'),
    weekDays: '',
    triggerOnDay: '',
    triggerOnWeekDay: '',
    isTriggerOnDay: true,
  },
};

export const initialcampaignState: campaignState = {
  selectedIds: [],
  campaignProducts: [],
  headerSteps: [],
  bodyPartKeysData: [],
  ctwaInfo: {},
  campaignType: '',
  mediafile: null,
  templateData: null,
  campaignName: '',
  templateCampaignType: '',
  screenRoute: '',
  campaignData: campaignInitialState,
  campaignDetailsData: undefined,
  campaignAudienceType: CampaignAudienceType.CUSTOMER,
  campaignScheduleDetails: undefined,
};

const CAMPAIGN_FEATURE_KEY = 'campaign';

export const campaignSlice = createSlice({
  name: CAMPAIGN_FEATURE_KEY,
  initialState: initialcampaignState,
  reducers: {
    updateSelectedIds(state, action: PayloadAction<number | number[]>) {
      if (isArray(action.payload)) {
        state.selectedIds = action.payload;
      } else {
        const idIndex = state.selectedIds.indexOf(action.payload);
        if (idIndex > -1) {
          state.selectedIds.splice(idIndex, 1);
        } else {
          state.selectedIds.push(action.payload);
        }
      }
    },
    updateCampaignsProductData(
      state,
      action: PayloadAction<ProductVariants[]>
    ) {
      if (!isEmpty(action.payload)) {
        state.campaignProducts = action.payload;
      } else {
        state.campaignProducts = [];
      }
    },
    removeSelectedIds(state) {
      state.selectedIds = [];
    },
    updateHeaderSteps(
      state,
      action: PayloadAction<{ id: number; label: string }[]>
    ) {
      if (!isEmpty(action.payload)) {
        state.headerSteps = action.payload;
      } else {
        state.headerSteps = [];
      }
    },
    updateTemplateCampaignData(
      state,
      action: PayloadAction<{
        bodyPartKeysData?: { key: string; label: string; value: string }[];
        ctwaInfo?: { [key: string]: string };
        mediafile?: {
          path: string;
          type: string;
        } | null;
        campaignType?: string;
        campaignName?: string;
      }>
    ) {
      const {
        bodyPartKeysData,
        ctwaInfo,
        mediafile,
        campaignType,
        campaignName,
      } = action.payload;

      state.bodyPartKeysData = defaultTo(bodyPartKeysData, []);
      state.ctwaInfo = defaultTo(ctwaInfo, {});
      state.mediafile = defaultTo(mediafile, null);
      state.campaignType = defaultTo(campaignType, '');
      state.campaignName = defaultTo(campaignName, '');
    },
    updateTemplateData(
      state,
      action: PayloadAction<WhatsappTemplateData | null>
    ) {
      state.templateData = action.payload ?? null;
    },
    updateTemplateCampaignType(state, action: PayloadAction<string>) {
      state.templateCampaignType = action.payload ?? '';
    },
    updateScreenRoute(state, action: PayloadAction<string>) {
      state.screenRoute = action.payload;
    },
    updateCampaignData(state, action: PayloadAction<CampaignDataProps>) {
      state.campaignData = action.payload;
    },
    onChangeCampaignData(
      state,
      action: PayloadAction<{ key: string; val: any }>
    ) {
      const data = {
        ...state.campaignData,
        [action.payload.key]: action.payload.val,
      };
      state.campaignData = data;
    },
    updateCampaignDetailsData(
      state,
      action: PayloadAction<CampaignDetails | undefined>
    ) {
      state.campaignDetailsData = action.payload;
    },
    updateCampaignAudienceType(
      state,
      action: PayloadAction<CampaignAudienceType>
    ) {
      state.campaignAudienceType = action.payload;
    },
    updateCampaignScheduleDetails(
      state,
      action: PayloadAction<CampaignScheduleDetails | undefined>
    ) {
      state.campaignScheduleDetails = action.payload;
    },
  },
});

export const campaignReducer = campaignSlice.reducer;

export const {
  updateSelectedIds,
  removeSelectedIds,
  updateCampaignsProductData,
  updateHeaderSteps,
  updateTemplateCampaignData,
  updateTemplateData,
  updateTemplateCampaignType,
  updateScreenRoute,
  updateCampaignData,
  onChangeCampaignData,
  updateCampaignDetailsData,
  updateCampaignAudienceType,
  updateCampaignScheduleDetails,
} = campaignSlice.actions;

const getcampaignState = (rootState: RootState): campaignState =>
  rootState[CAMPAIGN_FEATURE_KEY];

export const getSelectedIds = createSelector(
  getcampaignState,
  (state) => state.selectedIds
);

export const getCampaignsProduct = createSelector(
  getcampaignState,
  (state) => state.campaignProducts
);

export const getHeaderSteps = createSelector(
  getcampaignState,
  (state) => state.headerSteps
);

export const getBodyPartKeysData = createSelector(
  getcampaignState,
  (state) => state.bodyPartKeysData
);

export const getCtwaInfo = createSelector(
  getcampaignState,
  (state) => state.ctwaInfo
);

export const getMediafile = createSelector(
  getcampaignState,
  (state) => state.mediafile
);

export const getCampaignType = createSelector(
  getcampaignState,
  (state) => state.campaignType
);

export const getCampaignName = createSelector(
  getcampaignState,
  (state) => state.campaignName
);

export const getTemplateData = createSelector(
  getcampaignState,
  (state) => state.templateData
);

export const getTemplateCampaignType = createSelector(
  getcampaignState,
  (state) => state.templateCampaignType
);

export const getScreenRoute = createSelector(
  getcampaignState,
  (state) => state.screenRoute
);

export const getCampaignData = createSelector(
  getcampaignState,
  (state) => state.campaignData
);

export const getCampaignDetailsData = createSelector(
  getcampaignState,
  (state) => state.campaignDetailsData
);

export const getCampaignAudienceType = createSelector(
  getcampaignState,
  (state) => state.campaignAudienceType
);

export const getCampaignScheduleDetails = createSelector(
  getcampaignState,
  (state) => state.campaignScheduleDetails
);
